import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, ExternalLink, Smartphone, Tablet, Monitor, Home as HomeIcon, FileText, Compass, HelpCircle, Heart } from 'lucide-react';

/** Live iframe preview of the public site. Lets admin see exactly what supporters see,
 *  switch viewports, hop between pages, and open in a new tab without leaving the dashboard. */

const PAGES = [
  { path: '/',        label: 'Home',      Icon: HomeIcon },
  { path: '/journey', label: 'Journey',   Icon: FileText },
  { path: '/mission', label: 'Mission',   Icon: Compass },
  { path: '/signup',  label: 'Signup',    Icon: Heart },
  { path: '/faq',     label: 'FAQ',       Icon: HelpCircle },
];

const VIEWPORTS = [
  { id: 'desktop', label: 'Desktop', width: '100%',  Icon: Monitor },
  { id: 'tablet',  label: 'Tablet',  width: '820px', Icon: Tablet },
  { id: 'mobile',  label: 'Mobile',  width: '390px', Icon: Smartphone },
];

export const SitePreviewTab = () => {
  const [path, setPath] = useState('/');
  const [viewport, setViewport] = useState('desktop');
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef(null);

  // Same-origin iframe → use the current preview/production hostname dynamically.
  // The admin dashboard and the public site are served from the same domain.
  const [origin, setOrigin] = useState('');
  useEffect(() => { setOrigin(window.location.origin); }, []);

  const fullUrl = origin ? `${origin}${path}?_preview=${reloadKey}` : '';
  const viewportWidth = VIEWPORTS.find(v => v.id === viewport)?.width || '100%';

  return (
    <div data-testid="admin-site-preview-tab" className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-bold text-slate-900 font-['Outfit']">Public site preview</h2>
          </div>
          <span className="text-xs text-slate-500 hidden sm:inline">
            See exactly what supporters see. Edits made in other tabs reflect here on reload.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="site-preview-reload"
              onClick={() => setReloadKey(k => k + 1)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50"
              title="Refresh preview"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            {origin && (
              <a
                data-testid="site-preview-open-new"
                href={`${origin}${path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800"
                title="Open in new tab"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open in new tab
              </a>
            )}
          </div>
        </div>

        {/* Page picker */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {PAGES.map(p => {
            const Icon = p.Icon;
            return (
              <button
                key={p.path}
                data-testid={`site-preview-page-${p.label.toLowerCase()}`}
                onClick={() => setPath(p.path)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  path === p.path
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <Icon className="w-3 h-3" />
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Viewport picker */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500 mr-1">Viewport:</span>
          {VIEWPORTS.map(v => {
            const Icon = v.Icon;
            return (
              <button
                key={v.id}
                data-testid={`site-preview-viewport-${v.id}`}
                onClick={() => setViewport(v.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                  viewport === v.id
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                }`}
              >
                <Icon className="w-3 h-3" />
                {v.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Iframe — centered, with adjustable max width */}
      <div className="bg-slate-100 rounded-2xl border border-slate-200 p-3 sm:p-4 overflow-auto">
        <div
          className="mx-auto bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden transition-all"
          style={{ width: viewportWidth, maxWidth: '100%' }}
        >
          {fullUrl && (
            <iframe
              key={reloadKey + path + viewport}
              ref={iframeRef}
              data-testid="site-preview-iframe"
              src={fullUrl}
              title="Public site preview"
              className="w-full h-[80vh] block"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SitePreviewTab;
